
/**
 * Module dependencies.
 */

import { QueryObserverBaseResult, useQuery } from 'react-query';
import { fecthShowrooms } from './fetch-showrooms';
import { useMemo } from 'react';
import { useRouter } from 'next/router';

/**
 * `Props` type.
 */

type Props<P> = QueryObserverBaseResult<P, unknown>;

/**
 * `Options` type.
 */

type Options<P> = {
  initialData?: P,
  staleTime?: number
};

/**
 * Action type.
 */

const actionType = ({ locale }): string => {
  return `${locale}-showrooms`;
};

/**
 * Default stale time.
 *
 * Showrooms barely change and are read by the footer on every page, so without
 * this the site would hit the api again on every window focus.
 */

const defaultStaleTime = 30 * 60 * 1000;

/**
 * `useShowrooms` hook.
 */

function useShowrooms<P>(options?: Options<P>): Props<P> {
  const { locale } = useRouter();
  const variables = useMemo(() => ({ locale }), [locale]);
  const result = useQuery([actionType(variables), variables], () => {
    return fecthShowrooms(variables);
  }, {
    initialData: options?.initialData,
    staleTime: options?.staleTime ?? defaultStaleTime
  });

  return result;
}

/**
 * Export `useShowrooms` hook.
 */

export default useShowrooms;
